import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import './Funnel.css';
import { IconCheck, IconArrowRight } from './Icons';

/* -------------------------------------------------------------------------
   Offer terms — edit in one place. These strings are the only place the
   credit is described, so changing the amount or the conditions here changes
   it everywhere on this page.
   ------------------------------------------------------------------------- */

export const OFFER = {
  amount: 100,
  label: '$100 credit',
  short: '$100 off your build',
  terms:
    'Applied to your project total when you book. One per customer, and it holds for 90 days from the date we send your quote.',
};

/* -------------------------------------------------------------------------
   Steps
   ------------------------------------------------------------------------- */

const PACKAGES = {
  starter: { name: 'Starter', from: 700, blurb: 'Up to 3 pages, live in 3–5 days' },
  standard: { name: 'Standard', from: 1850, blurb: 'Fully custom, 4–6 pages, 1–2 weeks' },
  signature: { name: 'Signature', from: 3100, blurb: '3D and scroll-driven motion, 2–4 weeks' },
  unsure: { name: 'Not sure yet', from: null, blurb: 'We will recommend a tier on the call' },
};

const STEPS = [
  {
    id: 'projectType',
    kind: 'single',
    eyebrow: 'Step 1',
    question: 'What are you looking to build?',
    help: 'Pick the closest fit. Nothing here locks you in.',
    options: [
      { value: 'new', label: 'A new website', detail: 'Starting from nothing, or from a Facebook page' },
      { value: 'redesign', label: 'A redesign', detail: 'You have a site, it is not pulling its weight' },
      { value: 'website-marketing', label: 'Website + marketing', detail: 'Build it, then keep it fed' },
      { value: 'custom', label: 'Something custom', detail: 'App, internal system, 3D or interactive' },
    ],
  },
  {
    id: 'package',
    kind: 'single',
    eyebrow: 'Step 2',
    question: 'Which package is closest?',
    help: 'Starting prices. We confirm the real number in your quote.',
    options: [
      { value: 'starter', label: 'Starter', detail: PACKAGES.starter.blurb, price: 'from $700' },
      { value: 'standard', label: 'Standard', detail: PACKAGES.standard.blurb, price: 'from $1,850', badge: 'Most chosen' },
      { value: 'signature', label: 'Signature', detail: PACKAGES.signature.blurb, price: 'from $3,100' },
      { value: 'unsure', label: 'Not sure yet', detail: PACKAGES.unsure.blurb },
    ],
  },
  {
    id: 'timeline',
    kind: 'single',
    eyebrow: 'Step 3',
    question: 'When do you need it live?',
    help: 'This is the single biggest factor in how we schedule you.',
    options: [
      { value: 'asap', label: 'As soon as possible', detail: 'You have a deadline breathing down your neck' },
      { value: '2-4-weeks', label: 'In 2–4 weeks', detail: 'Planned, not panicked' },
      { value: '1-3-months', label: 'In 1–3 months', detail: 'Plenty of runway' },
      { value: 'exploring', label: 'Just exploring', detail: 'Gathering numbers for now' },
    ],
  },
  {
    id: 'assets',
    kind: 'multi',
    eyebrow: 'Step 4',
    question: 'What do you already have?',
    help: 'Select everything that applies. Missing pieces are not a problem — we will just price them in.',
    options: [
      { value: 'logo', label: 'A logo' },
      { value: 'brand', label: 'Brand colors / fonts' },
      { value: 'photos', label: 'Photos' },
      { value: 'copy', label: 'Written copy' },
      { value: 'domain', label: 'A domain' },
      { value: 'site', label: 'An existing site' },
      { value: 'none', label: 'None of it yet', exclusive: true },
    ],
  },
  {
    id: 'details',
    kind: 'form',
    eyebrow: 'Step 5',
    question: 'Where do we send the quote?',
    help: 'Last step. Your credit is issued the moment this goes through.',
  },
];

const TIMELINE_LABEL = {
  asap: 'As soon as possible',
  '2-4-weeks': 'In 2–4 weeks',
  '1-3-months': 'In 1–3 months',
  exploring: 'Just exploring',
};

const PROJECT_LABEL = {
  new: 'A new website',
  redesign: 'A redesign',
  'website-marketing': 'Website + marketing',
  custom: 'Something custom',
};

const ASSET_LABEL = {
  logo: 'A logo',
  brand: 'Brand colors / fonts',
  photos: 'Photos',
  copy: 'Written copy',
  domain: 'A domain',
  site: 'An existing site',
  none: 'Nothing yet',
};

const STORAGE_KEY = 'smFunnelProgress';

const EMPTY = {
  projectType: '',
  package: '',
  timeline: '',
  assets: [],
  name: '',
  business: '',
  email: '',
  phone: '',
  notes: '',
};

// Short, human-readable, and unique enough to look up against an inbox.
const makeCreditCode = () =>
  `SM${OFFER.amount}-${Math.random().toString(36).slice(2, 6).toUpperCase()}${Date.now()
    .toString(36)
    .slice(-3)
    .toUpperCase()}`;

// Pure, so it can run with a freshly minted code at submit time instead of
// waiting for a React render to land the value in the DOM.
function buildMessage(data, creditCode) {
    const pkg = PACKAGES[data.package];
    const lines = [
      'NEW LEAD FROM THE GET STARTED FUNNEL',
      '',
      `Project type:   ${PROJECT_LABEL[data.projectType] || '—'}`,
      `Package:        ${pkg ? pkg.name : '—'}${pkg && pkg.from ? ` (from $${pkg.from.toLocaleString()})` : ''}`,
      `Timeline:       ${TIMELINE_LABEL[data.timeline] || '—'}`,
      `Already has:    ${
        data.assets.length ? data.assets.map((a) => ASSET_LABEL[a]).join(', ') : '—'
      }`,
      '',
      `Name:           ${data.name || '—'}`,
      `Business:       ${data.business || '—'}`,
      `Email:          ${data.email || '—'}`,
      `Phone:          ${data.phone || '—'}`,
      '',
      'Notes:',
      data.notes || '(none)',
      '',
      `${OFFER.label.toUpperCase()} EARNED — code: ${creditCode || '(pending)'}`,
      OFFER.terms,
    ];
    return lines.join('\n');
}

/* -------------------------------------------------------------------------
   Component
   ------------------------------------------------------------------------- */

const Funnel = () => {
  const formRef = useRef(null);
  const headingRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);
  const [creditCode, setCreditCode] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [touched, setTouched] = useState(false);

  const done = step >= STEPS.length;
  const current = STEPS[step];

  /* ---- Restore in-progress answers, and honour ?package= ---------------- */
  useEffect(() => {
    let restored = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) restored = JSON.parse(raw);
    } catch {
      restored = null;
    }

    const preset = searchParams.get('package');
    setData((prev) => ({
      ...prev,
      ...(restored || {}),
      ...(preset && PACKAGES[preset] ? { package: preset } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- Persist so a refresh does not cost them their answers ------------ */
  useEffect(() => {
    if (done) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* private mode — carry on without persistence */
    }
  }, [data, done]);

  /* ---- Move focus to the new question so keyboard users keep their place */
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const set = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  const toggleAsset = (value, exclusive) => {
    setData((prev) => {
      if (exclusive) return { ...prev, assets: prev.assets.includes(value) ? [] : [value] };
      const without = prev.assets.filter((a) => a !== value && a !== 'none');
      return {
        ...prev,
        assets: prev.assets.includes(value) ? without : [...without, value],
      };
    });
  };

  const isValidEmail = (email) => /.+@.+\..+/.test(email);

  const canAdvance = useMemo(() => {
    if (!current) return false;
    if (current.kind === 'single') return Boolean(data[current.id]);
    if (current.kind === 'multi') return data.assets.length > 0;
    return Boolean(data.name.trim()) && isValidEmail(data.email.trim());
  }, [current, data]);

  /* ---- The message body that actually lands in the inbox ---------------- */
  const summaryMessage = useMemo(
    () => buildMessage(data, creditCode),
    [data, creditCode]
  );

  const estimate = useMemo(() => {
    const pkg = PACKAGES[data.package];
    if (!pkg || !pkg.from) return null;
    return { from: pkg.from, after: pkg.from - OFFER.amount, name: pkg.name };
  }, [data.package]);

  /* ---- Navigation -------------------------------------------------------- */

  const next = () => {
    setTouched(true);
    if (!canAdvance) return;
    setTouched(false);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setTouched(false);
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canAdvance || sending) return;

    // Honeypot — bots fill everything they find.
    if (formRef.current.website && formRef.current.website.value) {
      setStatus('Something went wrong. Please email us directly.');
      return;
    }
    if (!recaptchaToken) {
      setStatus('Please complete the reCAPTCHA so we know you are human.');
      return;
    }

    // Mint the code before sending so it travels with the email. The hidden
    // input is React-controlled, so write the finished body into the DOM and
    // send in the same tick — waiting on a re-render here would race the
    // serialiser and mail out a code of "(pending)".
    const code = makeCreditCode();
    formRef.current.message.value = buildMessage(data, code);
    setCreditCode(code);
    setSending(true);
    setStatus('Sending…');

    emailjs
        .sendForm('service_vf3zkq2', 'template_mgxj4gm', formRef.current, 'AGlcs9Px5S9iDW5bM')
        .then(
          () => {
            setSending(false);
            setStatus('');
            try {
              localStorage.removeItem(STORAGE_KEY);
              localStorage.setItem('smCreditCode', code);
            } catch {
              /* ignore */
            }
            setStep(STEPS.length);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          },
          () => {
            setSending(false);
            setCreditCode('');
            setStatus(
              'That did not go through. Please try again, or email shawnysmedia@gmail.com and we will honour the credit anyway.'
            );
          }
      );
  };

  const restart = () => {
    setData(EMPTY);
    setCreditCode('');
    setRecaptchaToken('');
    setStatus('');
    setStep(0);
  };

  const progress = done ? 1 : step / STEPS.length;

  /* ---- Success ----------------------------------------------------------- */

  if (done) {
    return (
      <main className="funnel funnel--done" id="main">
        <div className="funnel-grid-bg" aria-hidden="true" />
        <div className="container funnel-success">
          <span className="funnel-success-mark" aria-hidden="true">
            <IconCheck />
          </span>

          <p className="eyebrow eyebrow--plain">You are in</p>
          <h1 className="funnel-success-title">
            Your {OFFER.label} is locked in.
          </h1>
          <p className="funnel-success-lede">
            We have everything we need. Expect a fixed quote in your inbox within 48
            hours — with the credit already taken off the total.
          </p>

          <div className="credit-card">
            <span className="credit-card-label">Your credit code</span>
            <strong className="credit-card-code">{creditCode}</strong>
            <p className="credit-card-terms">{OFFER.terms}</p>
          </div>

          <ol className="funnel-next" data-stagger="0.07">
            <li className="reveal reveal-up">
              <span>01</span>
              <div>
                <h2>We read it properly</h2>
                <p>A person goes through your answers — not an autoresponder.</p>
              </div>
            </li>
            <li className="reveal reveal-up">
              <span>02</span>
              <div>
                <h2>You get a fixed quote</h2>
                <p>Within 48 hours, with the {OFFER.label} already applied.</p>
              </div>
            </li>
            <li className="reveal reveal-up">
              <span>03</span>
              <div>
                <h2>You decide</h2>
                <p>No pressure. If a cheaper tier suits you better, we will say so.</p>
              </div>
            </li>
          </ol>

          <div className="btn-row funnel-success-actions">
            <a
              href="https://calendly.com/shawnysmedia/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--primary btn--lg"
            >
              Book a 30-min call <span className="btn-arrow">→</span>
            </a>
            <Link to="/live-apps" className="btn btn--ghost btn--lg">
              See our live work
            </Link>
          </div>

          <button type="button" className="btn btn--link funnel-restart" onClick={restart}>
            Start another project
          </button>
        </div>
      </main>
    );
  }

  /* ---- Steps -------------------------------------------------------------- */

  return (
    <main className="funnel" id="main">
      <div className="funnel-grid-bg" aria-hidden="true" />
      <div className="funnel-orb" data-parallax="0.05" aria-hidden="true" />

      <div className="container funnel-shell">
        {/* ---- Question column ---- */}
        <form ref={formRef} className="funnel-main" onSubmit={handleSubmit} autoComplete="off">
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex="-1"
            autoComplete="off"
            style={{ display: 'none' }}
          />
          {/* Mirrors for the EmailJS template */}
          <input type="hidden" name="subject" value="New lead — Get Started funnel" readOnly />
          <input type="hidden" name="message" value={summaryMessage} readOnly />

          <div className="funnel-progress">
            <div className="funnel-progress-meta">
              <span>{current.eyebrow}</span>
              <span>
                {step + 1} / {STEPS.length}
              </span>
            </div>
            <div className="funnel-progress-track">
              <span
                className="funnel-progress-bar"
                style={{ transform: `scaleX(${progress})` }}
              />
            </div>
          </div>

          <h1 className="funnel-question" tabIndex={-1} ref={headingRef}>
            {current.question}
          </h1>
          <p className="funnel-help">{current.help}</p>

          {/* Single choice */}
          {current.kind === 'single' && (
            <div className="option-grid" role="radiogroup" aria-label={current.question}>
              {current.options.map((opt) => {
                const checked = data[current.id] === opt.value;
                return (
                  <label className={`option${checked ? ' is-checked' : ''}`} key={opt.value}>
                    <input
                      type="radio"
                      name={current.id}
                      value={opt.value}
                      checked={checked}
                      onChange={() => set(current.id, opt.value)}
                    />
                    <span className="option-body">
                      <span className="option-top">
                        <span className="option-label">{opt.label}</span>
                        {opt.badge && <span className="option-badge">{opt.badge}</span>}
                        {opt.price && <span className="option-price">{opt.price}</span>}
                      </span>
                      <span className="option-detail">{opt.detail}</span>
                    </span>
                    <span className="option-tick" aria-hidden="true">
                      <IconCheck />
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Multi choice */}
          {current.kind === 'multi' && (
            <div className="chip-grid">
              {current.options.map((opt) => {
                const checked = data.assets.includes(opt.value);
                return (
                  <label className={`chip${checked ? ' is-checked' : ''}`} key={opt.value}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAsset(opt.value, opt.exclusive)}
                    />
                    <span className="chip-tick" aria-hidden="true">
                      <IconCheck />
                    </span>
                    {opt.label}
                  </label>
                );
              })}
            </div>
          )}

          {/* Details */}
          {current.kind === 'form' && (
            <div className="funnel-fields">
              <div className="funnel-field-row">
                <div className="funnel-field">
                  <label className="field-label" htmlFor="f-name">
                    Your name *
                  </label>
                  <input
                    id="f-name"
                    className="field"
                    type="text"
                    name="name"
                    autoComplete="name"
                    placeholder="Jane Rivera"
                    value={data.name}
                    onChange={(e) => set('name', e.target.value)}
                  />
                </div>
                <div className="funnel-field">
                  <label className="field-label" htmlFor="f-business">
                    Business name
                  </label>
                  <input
                    id="f-business"
                    className="field"
                    type="text"
                    name="business"
                    autoComplete="organization"
                    placeholder="Rivera Landscaping"
                    value={data.business}
                    onChange={(e) => set('business', e.target.value)}
                  />
                </div>
              </div>

              <div className="funnel-field-row">
                <div className="funnel-field">
                  <label className="field-label" htmlFor="f-email">
                    Email *
                  </label>
                  <input
                    id="f-email"
                    className="field"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="jane@rivera.com"
                    value={data.email}
                    onChange={(e) => set('email', e.target.value)}
                  />
                </div>
                <div className="funnel-field">
                  <label className="field-label" htmlFor="f-phone">
                    Phone <em>optional</em>
                  </label>
                  <input
                    id="f-phone"
                    className="field"
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    placeholder="(607) 555-0134"
                    value={data.phone}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                </div>
              </div>

              <div className="funnel-field">
                <label className="field-label" htmlFor="f-notes">
                  Anything else we should know? <em>optional</em>
                </label>
                <textarea
                  id="f-notes"
                  className="field"
                  name="notes"
                  rows="4"
                  placeholder="A site you like, a deadline, a page you know you need…"
                  value={data.notes}
                  onChange={(e) => set('notes', e.target.value)}
                />
              </div>

              <div className="funnel-recaptcha">
                <ReCAPTCHA
                  sitekey="6LewK3MrAAAAAAmQpMVTBm0mTlqILi1PglrOwkmi"
                  theme="dark"
                  onChange={(token) => setRecaptchaToken(token)}
                />
              </div>
            </div>
          )}

          {touched && !canAdvance && (
            <p className="funnel-error" role="alert">
              {current.kind === 'form'
                ? 'We need a name and a valid email to send your quote.'
                : 'Pick an option to continue.'}
            </p>
          )}

          {status && (
            <p className="funnel-error funnel-error--status" role="status">
              {status}
            </p>
          )}

          <div className="funnel-nav">
            {step > 0 ? (
              <button type="button" className="btn btn--ghost" onClick={back}>
                Back
              </button>
            ) : (
              <Link to="/products" className="btn btn--ghost">
                See pricing first
              </Link>
            )}

            {current.kind === 'form' ? (
              <button type="submit" className="btn btn--primary btn--lg" disabled={sending}>
                {sending ? 'Sending…' : `Claim my ${OFFER.label}`}
                {!sending && <span className="btn-arrow">→</span>}
              </button>
            ) : (
              <button type="button" className="btn btn--primary btn--lg" onClick={next}>
                Continue <span className="btn-arrow">→</span>
              </button>
            )}
          </div>
        </form>

        {/* ---- Running summary ---- */}
        <aside className="funnel-aside">
          <div className="funnel-offer">
            <span className="funnel-offer-amount">${OFFER.amount}</span>
            <span className="funnel-offer-text">
              credit, yours the moment you finish these {STEPS.length} steps.
            </span>
          </div>

          <div className="funnel-recap">
            <h2>Your answers</h2>
            <dl>
              <div className={data.projectType ? 'is-set' : ''}>
                <dt>Project</dt>
                <dd>{PROJECT_LABEL[data.projectType] || 'Not yet'}</dd>
              </div>
              <div className={data.package ? 'is-set' : ''}>
                <dt>Package</dt>
                <dd>{PACKAGES[data.package]?.name || 'Not yet'}</dd>
              </div>
              <div className={data.timeline ? 'is-set' : ''}>
                <dt>Timeline</dt>
                <dd>{TIMELINE_LABEL[data.timeline] || 'Not yet'}</dd>
              </div>
              <div className={data.assets.length ? 'is-set' : ''}>
                <dt>You have</dt>
                <dd>
                  {data.assets.length
                    ? data.assets.map((a) => ASSET_LABEL[a]).join(', ')
                    : 'Not yet'}
                </dd>
              </div>
            </dl>
          </div>

          {estimate && (
            <div className="funnel-estimate">
              <span className="funnel-estimate-label">Starting estimate</span>
              <div className="funnel-estimate-math">
                <span className="funnel-estimate-from">${estimate.from.toLocaleString()}</span>
                <span className="funnel-estimate-minus">− ${OFFER.amount} credit</span>
              </div>
              <strong className="funnel-estimate-total">
                ${estimate.after.toLocaleString()}
              </strong>
              <p className="funnel-estimate-note">
                Starting price for {estimate.name}, not a quote. We confirm the real
                number within 48 hours.
              </p>
            </div>
          )}

          <p className="funnel-assure">
            <IconArrowRight />
            No payment now. No obligation. You own everything we build.
          </p>
        </aside>
      </div>
    </main>
  );
};

export default Funnel;
